import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Diamond } from 'lucide-react';

// Same suco-level "get-alert" endpoint and variable mapping used by ForecastMap.js
const VARIABLE_MAP = {
  rainfall: 'tp',
  max_temp: 'heat',
  min_temp: 'cold',
  humidity: 'thi',
};
const SUCO_ADMIN_LEVEL = 3; // matches ForecastMap.getAdminLevel('suco')

// Timor-Leste is UTC+9 with no DST, so this always reflects the current local date there.
const getTimorTodayDateStr = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Dili',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const Bulletin = () => {
  const [nonSpatialData, setNonSpatialData] = useState({});
  const [loading, setLoading] = useState(false);
  const marqueeRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || '';

  const fetchSucoWeatherData = useCallback(async () => {
    setLoading(true);
    try {
      const fetched = await Promise.all(
        Object.entries(VARIABLE_MAP).map(([paramId, varName]) =>
          fetch(`${backendUrl}forecastmodels/get-alert/?variable=${varName}&admin_level=${SUCO_ADMIN_LEVEL}`)
            .then((r) => { if (!r.ok) throw new Error(`Failed for ${varName}`); return r.json(); })
            .then((data) => ({ paramId, data }))
            .catch((err) => { console.error(err); return null; })
        )
      );

      const results = {};
      fetched.forEach((result) => {
        if (!result?.data?.data) return;
        results[result.paramId] = result.data.data;
      });
      setNonSpatialData(results);
    } catch (error) {
      console.error('Error fetching Suco weather data:', error);
      setNonSpatialData({});
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  // Buckets mirror ForecastMap.js's PARAM_RANGES thresholds for each parameter.
  const calculateAlertCounts = useCallback(
    (paramId) => {
      const counts = { veryHigh: 0, high: 0, moderate: 0, noEffect: 0 };
      const paramData = nonSpatialData[paramId];
      if (!paramData) return counts;

      const todayStr = getTimorTodayDateStr();

      Object.values(paramData).forEach((records) => {
        if (!Array.isArray(records) || !records.length) return;
        // Use the forecast period whose start date is today in Timor; fall back
        // to the first available period if today isn't in the returned range.
        const record = records.find((r) => (r.period_start || '').slice(0, 10) === todayStr) || records[0];
        const value = record?.aggregate_value;
        if (value == null || isNaN(value)) {
          counts.noEffect++;
          return;
        }

        const num = Number(value);
        if (paramId === 'rainfall') {
          if (num >= 60) counts.veryHigh++;
          else if (num >= 25) counts.high++;
          else if (num >= 15) counts.moderate++;
          else counts.noEffect++;
        } else if (paramId === 'max_temp') {
          if (num >= 40) counts.veryHigh++;
          else if (num >= 30) counts.high++;
          else if (num >= 20) counts.moderate++;
          else counts.noEffect++;
        } else if (paramId === 'min_temp') {
          if (num < 0) counts.veryHigh++;
          else if (num < 10) counts.high++;
          else if (num < 20) counts.moderate++;
          else counts.noEffect++;
        } else if (paramId === 'humidity') {
          if (num >= 33) counts.veryHigh++;
          else if (num >= 30) counts.high++;
          else if (num >= 27) counts.moderate++;
          else counts.noEffect++;
        }
      });

      return counts;
    },
    [nonSpatialData]
  );

  const alertCounts = useMemo(
    () => ({
      rainfall: calculateAlertCounts('rainfall'),
      max_temp: calculateAlertCounts('max_temp'),
      min_temp: calculateAlertCounts('min_temp'),
      humidity: calculateAlertCounts('humidity'),
    }),
    [calculateAlertCounts]
  );

  const news = useMemo(
    () =>
      alertCounts
        ? [
            {
              label: 'Rainfall Alerts',
              counts: {
                veryHigh: alertCounts.rainfall?.veryHigh || 0,
                high: alertCounts.rainfall?.high || 0,
                moderate: alertCounts.rainfall?.moderate || 0,
                noEffect: alertCounts.rainfall?.noEffect || 0,
              },
            },
            {
              label: 'Maximum Temp Alerts',
              counts: {
                veryHigh: alertCounts.max_temp?.veryHigh || 0,
                high: alertCounts.max_temp?.high || 0,
                moderate: alertCounts.max_temp?.moderate || 0,
                noEffect: alertCounts.max_temp?.noEffect || 0,
              },
            },
            {
              label: 'Minimum Temp Alerts',
              counts: {
                veryHigh: alertCounts.min_temp?.veryHigh || 0,
                high: alertCounts.min_temp?.high || 0,
                moderate: alertCounts.min_temp?.moderate || 0,
                noEffect: alertCounts.min_temp?.noEffect || 0,
              },
            },
            {
              label: 'Humidity Alerts',
              counts: {
                veryHigh: alertCounts.humidity?.veryHigh || 0,
                high: alertCounts.humidity?.high || 0,
                moderate: alertCounts.humidity?.moderate || 0,
                noEffect: alertCounts.humidity?.noEffect || 0,
              },
            },
          ]
        : [],
    [alertCounts]
  );

  useEffect(() => {
    fetchSucoWeatherData();
  }, [fetchSucoWeatherData]);

  useEffect(() => {
    const marquee = marqueeRef.current;
    let animationFrameId;
    let lastTime = performance.now();

    if (marquee && news.length > 0) {
      const contentWidth = marquee.scrollWidth / 3;
      let position = 0;
      const speed = 60;

      const animate = (currentTime) => {
        const deltaTime = (currentTime - lastTime) / 1000;
        position -= speed * deltaTime;
        if (position <= -contentWidth) {
          position += contentWidth;
        }
        marquee.style.transform = `translateX(${position}px)`;
        lastTime = currentTime;
        animationFrameId = requestAnimationFrame(animate);
      };

      animationFrameId = requestAnimationFrame(animate);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [news]);

  return (
    <div
      style={{
        height: '40px',
        width: '100%',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        borderRadius: '8px',
      }}
    >
      <div
        style={{
          backgroundColor: 'rgb(37, 99, 235)',
          color: 'white',
          fontWeight: 'bold',
          padding: '0 15px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '8px 0px 0px 8px',
        }}
      >
        Weather Updates
      </div>
      <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}>
        <div
          ref={marqueeRef}
          style={{
            display: 'inline-block',
            padding: '0 15px',
            color: '#424242',
            fontSize: '14px',
            fontWeight: '500',
            willChange: 'transform',
          }}
        >
          {loading ? (
            <span style={{ color: '#D1D5DB', fontSize: '14px', fontWeight: '500' }}>
              Loading weather alerts...
            </span>
          ) : news.length > 0 ? (
            [...news, ...news, ...news].map((item, index) => (
              <span
                key={index}
                style={{
                  marginRight: '15px',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    backgroundColor: '#f0f7f7ff',
                    borderRadius: '8px',
                    padding: '2px 8px',
                    marginRight: '8px',
                  }}
                >
                  {item.label}
                </span>
                <span style={{ marginRight: '8px' }}>
                  Very High: {item.counts.veryHigh}
                </span>
                <span style={{ marginRight: '8px' }}>
                  High: {item.counts.high}
                </span>
                <span style={{ marginRight: '8px' }}>
                  Moderate: {item.counts.moderate}
                </span>
                <span style={{ marginRight: '8px' }}>
                  No Effect: {item.counts.noEffect}
                </span>
                <Diamond
                  size={14}
                  style={{ marginLeft: '8px', color: '#3B82F6', fill: '#3B82F6' }}
                />
              </span>
            ))
          ) : (
            <span style={{ color: '#D1D5DB', fontSize: '14px', fontWeight: '500' }}>
              No weather alerts available
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bulletin;